import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useEffect } from 'react';

import {
  type ObjectPermissionMutationInput,
  ObjectPermissionMutationService,
} from '../../permissions';
import { WorkspaceService } from '../../workspace';
import { useConfirmAppDataChange } from './app-data-change-confirmation';
import {
  type AppResourceLifecycleImpact,
  fetchPermissionChangeAppResourceLifecycleImpacts,
  removesAppResourceReadAccess,
} from './app-resource-lifecycle';

/**
 * Warns before a role change removes read access that apps rely on. A data
 * binding stays valid only while the app owner can read the host page, so
 * revoking the owner's access, directly or through a group or folder, cuts the
 * app off for every viewer. Every
 * page and folder permission change runs through the gate, which asks only
 * when the change removes read access from such an owner. Cancelling rejects
 * the change with an `isAppDataChangeCancelled` error.
 */
export function useAppDataPermissionChangeGate(): void {
  const workspaceService = useService(WorkspaceService);
  const permissionMutation = useService(ObjectPermissionMutationService);
  const confirmAppDataChange = useConfirmAppDataChange();
  const t = useI18n();

  useEffect(
    () =>
      permissionMutation.registerChangeGate(async input => {
        const workspace = workspaceService.workspace;
        if (
          workspace.flavour === 'local' ||
          !removesAppResourceReadAccess(input)
        ) {
          return;
        }
        await confirmAppDataChange(
          await fetchPermissionChangeImpacts(workspace.id, input),
          {
            confirm: t['com.streamlain.appDataPermissionChange.confirm'](),
            description:
              t['com.streamlain.appDataPermissionChange.description'](),
            title: t['com.streamlain.appDataPermissionChange.title'](),
          }
        );
      }),
    [confirmAppDataChange, permissionMutation, t, workspaceService]
  );
}

async function fetchPermissionChangeImpacts(
  workspaceId: string,
  input: ObjectPermissionMutationInput
): Promise<AppResourceLifecycleImpact[]> {
  const { principal } = input;
  // A Mate owns no app: its roles never cut an app off from its data.
  if (principal.kind === 'mate') return [];
  try {
    return await fetchPermissionChangeAppResourceLifecycleImpacts(workspaceId, {
      action: input.action,
      principal: { id: principal.id, kind: principal.kind },
      role: input.role,
      target: input.target,
    });
  } catch (error) {
    // The warning is advisory. Cloud permission mutations still require the
    // canonical online preflight even when this impact lookup is unavailable.
    console.warn('App data permission change impacts unavailable', error);
    return [];
  }
}
